import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  Barcode,
  formatCurrencyWithCurrencyCode,
  StoreProduct,
  StoreProductFraction,
} from '@bofrak-backend/shared';
import { apiAdapter, barcodeToPLU, getErrorMessage } from '@bofrak-backend/shared-ui';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FaBarcode, FaDownload, FaHashtag, FaQrcode } from 'react-icons/fa';
import { useRecoilValue } from 'recoil';
import { adminAtom } from '../../recoil/atoms';
import BarcodeEditor from './BarcodeEditor';
import BarcodeLabelPreview, { CodeDisplayMode } from './BarcodeLabelPreview';
import { downloadBarcodeLabelPdf, BarcodeLabelData } from './generateBarcodeLabelPdf';
import { buildLabelData, labelFileNameFromProduct, productDisplayName } from './labelHelpers';

/* ─── Types ───────────────────────────────────────────────────────── */

interface BarcodeManagerModalProps {
  product: StoreProduct;
  isOpen: boolean;
  onClose: () => void;
  /** called after a successful barcode save so the parent can refetch */
  onRefresh?: () => void;
  /** When true (default), only display the default barcode in read-only mode */
  onlyShowDefault?: boolean;
}

type PriceCallback = (withPrice: boolean) => void;

/* ─── Component ───────────────────────────────────────────────────── */

const BarcodeManagerModal: React.FC<BarcodeManagerModalProps> = ({
  product,
  isOpen,
  onClose,
  onRefresh,
  onlyShowDefault = true,
}) => {
  const toast = useToast();
  const employee = useRecoilValue(adminAtom);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [codeMode, setCodeMode] = useState<CodeDisplayMode>('qrcode');

  // ─── price-confirmation dialog ──────────────────────────────────
  const {
    isOpen: priceDialogOpen,
    onOpen: openPriceDialog,
    onClose: closePriceDialog,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pendingCb = useRef<PriceCallback | null>(null);

  // ─── children memo (stable reference) ───────────────────────────
  const children = useMemo<StoreProductFraction[]>(
    () => product.store_product_fractions ?? [],
    [product.store_product_fractions],
  );

  // ─── default labels for parent + children (onlyShowDefault mode) ──
  const defaultEntries = useMemo<
    { item: StoreProduct | StoreProductFraction; barcode: Barcode; label: BarcodeLabelData }[]
  >(() => {
    const entries: { item: StoreProduct | StoreProductFraction; barcode: Barcode; label: BarcodeLabelData }[] = [];
    const items: (StoreProduct | StoreProductFraction)[] = [product, ...children];
    for (const item of items) {
      const bc = item.barcodes?.find((b) => b.type === 'default');
      if (!bc) continue;
      const labels = buildLabelData(item, formatCurrencyWithCurrencyCode);
      const lbl = labels.find((l) => l.value === String(bc.value));
      if (lbl) entries.push({ item, barcode: bc, label: lbl });
    }
    return entries;
  }, [product, children]);

  // ─── sold-by-weight check (parent or any child) ─────────────────
  const isSoldByWeight = useMemo(
    () =>
      product.is_sold_by_weight ||
      children.some((c) => c.is_sold_by_weight),
    [product.is_sold_by_weight, children],
  );

  // ─── PLU entries for parent + children sold by weight ───────────
  const pluEntries = useMemo(() => {
    const entries: { name: string; plu: string; price: string }[] = [];
    const items: (StoreProduct | StoreProductFraction)[] = [product, ...children];
    for (const item of items) {
      if (!item.is_sold_by_weight) continue;
      const bc = item.barcodes?.find((b) => b.type === 'default');
      if (!bc) continue;
      const val = String(bc.value).trim();
      if (!val) continue;
      entries.push({
        name: productDisplayName(item),
        plu: barcodeToPLU(val).toString(),
        price: formatCurrencyWithCurrencyCode(item.price),
      });
    }
    return entries;
  }, [product, children]);

  /**
   * Prompt the user whether to include prices, then invoke the callback.
   * If the caller already knows, it skips the dialog.
   */
  const askPrice = useCallback(
    (cb: PriceCallback) => {
      pendingCb.current = cb;
      openPriceDialog();
    },
    [openPriceDialog],
  );

  const answerPrice = (withPrice: boolean) => {
    closePriceDialog();
    pendingCb.current?.(withPrice);
    pendingCb.current = null;
  };

  // ─── helpers ────────────────────────────────────────────────────
  const fmt = formatCurrencyWithCurrencyCode;

  const makeSaveHandler =
    (targetId: string, merchantId: string) =>
    async (payload: {
      add_barcodes?: Barcode[];
      remove_barcodes?: Barcode[];
    }) => {
      if (!employee?.id) {
        toast({
          title: 'Not authenticated',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      setSavingId(targetId);
      try {
        await apiAdapter.updateProduct({
          id: targetId,
          merchant_id: merchantId,
          updator_id: employee.id,
          ...payload,
        });
        toast({ title: 'Barcodes saved', status: 'success', duration: 2000 });
        onRefresh?.();
      } catch (err: unknown) {
        toast({
          title: 'Save failed',
          description: getErrorMessage(err),
          status: 'error',
          duration: 4000,
        });
      } finally {
        setSavingId(null);
      }
    };

  const allLabels = useCallback((): BarcodeLabelData[] => {
    const parentLabels = buildLabelData(product, fmt);
    const childLabels = children.flatMap((c) => buildLabelData(c, fmt));
    return [...parentLabels, ...childLabels];
  }, [product, children, fmt]);

  const handleDownloadAll = () => {
    askPrice((withPrice) => {
      const labels = allLabels();
      if (!labels.length) {
        toast({ title: 'No barcodes to download', status: 'info' });
        return;
      }
      downloadBarcodeLabelPdf(
        labels,
        withPrice,
        labelFileNameFromProduct(codeMode, product),
        codeMode,
      );
    });
  };

  // ─── single accordion section ──────────────────────────────────
  const renderSection = (
    item: StoreProduct | StoreProductFraction,
    headerColor: string,
    label: string,
  ) => {
    const labels = buildLabelData(item, fmt);
    const barcodes: Barcode[] = (item.barcodes as Barcode[]) ?? [];

    return (
      <AccordionItem key={item.id} border="none" mb={2}>
        <AccordionButton
          bg={headerColor}
          color="white"
          borderRadius="md"
          _hover={{ opacity: 0.9 }}
          _expanded={{ borderBottomRadius: 0 }}
          px={3}
          py={2}>
          <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
            {label}
          </Box>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel
          pb={4}
          px={3}
          border="1px solid"
          borderColor="gray.200"
          borderTop="none"
          borderBottomRadius="md">
          <VStack align="stretch" spacing={4}>
            {/* Barcode editor */}
            <BarcodeEditor
              barcodes={barcodes}
              onlyDefault={true}
              storeId={item.store_id}
              productId={item.id}
              onSave={makeSaveHandler(item.id, item.merchant_id)}
              isLoading={savingId === item.id}
            />

            {/* Label previews */}
            {labels.length > 0 && (
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">
                    Label Previews
                  </Text>
                  <Button
                    size="xs"
                    leftIcon={<FaDownload size={10} />}
                    colorScheme="blue"
                    variant="outline"
                    display={"none"}
                    onClick={() =>
                      askPrice((wp) =>
                        downloadBarcodeLabelPdf(
                          labels,
                          wp,
                          labelFileNameFromProduct(codeMode, item),
                          codeMode,
                        ),
                      )
                    }>
                    Download All
                  </Button>
                </Flex>

                <SimpleGrid columns={[1, 2]} spacing={3}>
                  {labels.map((lbl) => (
                    <BarcodeLabelPreview
                      key={lbl.value}
                      label={lbl}
                      includePrice={true}
                      onAskPrice={askPrice}
                      codeMode={codeMode}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    );
  };

  // ─── render ─────────────────────────────────────────────────────
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader fontSize="md" pb={2}>
            <HStack spacing={2} justify="space-between" w="full" pr={8}>
              <HStack spacing={2}>
                {codeMode === 'qrcode' ? <FaQrcode /> : codeMode === 'plu' ? <FaHashtag /> : <FaBarcode />}
                <Text>Label Manager</Text>
              </HStack>
              <ButtonGroup size="xs" isAttached variant="outline">
                <Tooltip label="QR Code" hasArrow>
                  <IconButton
                    aria-label="QR Code"
                    icon={<FaQrcode />}
                    colorScheme={codeMode === 'qrcode' ? 'blue' : 'gray'}
                    variant={codeMode === 'qrcode' ? 'solid' : 'outline'}
                    onClick={() => setCodeMode('qrcode')}
                  />
                </Tooltip>
                <Tooltip label="Barcode" hasArrow>
                  <IconButton
                    aria-label="Barcode"
                    icon={<FaBarcode />}
                    colorScheme={codeMode === 'barcode' ? 'blue' : 'gray'}
                    variant={codeMode === 'barcode' ? 'solid' : 'outline'}
                    onClick={() => setCodeMode('barcode')}
                  />
                </Tooltip>
                {isSoldByWeight && (
                  <Tooltip label="PLU (Price Look-Up)" hasArrow>
                    <IconButton
                      aria-label="PLU"
                      icon={<FaHashtag />}
                      colorScheme={codeMode === 'plu' ? 'teal' : 'gray'}
                      variant={codeMode === 'plu' ? 'solid' : 'outline'}
                      onClick={() => setCodeMode('plu')}
                    />
                  </Tooltip>
                )}
              </ButtonGroup>
            </HStack>
            <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
              {product.name}
            </Text>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            {codeMode === 'plu' ? (
              <VStack align="stretch" spacing={3}>
                {pluEntries.length > 0 ? (
                  pluEntries.map((entry) => (
                    <Box
                      key={entry.plu}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="gray.200"
                      bg="gray.50">
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {entry.name}
                      </Text>
                      <HStack justify="space-between" align="baseline" mt={1}>
                        <HStack spacing={2} align="baseline">
                          <Text fontSize="xs" color="gray.500">
                            PLU
                          </Text>
                          <Text
                            fontSize="2xl"
                            fontFamily="mono"
                            fontWeight="bold"
                            color="teal.600">
                            {entry.plu}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {entry.price}
                        </Text>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={4}>
                    No products sold by weight.
                  </Text>
                )}
              </VStack>
            ) : onlyShowDefault ? (
              <VStack align="stretch" spacing={4}>
                {defaultEntries.length > 0 ? (
                  <>
                    {/* Download all defaults */}
                    {defaultEntries.length > 1 && (
                      <Flex justify="flex-end">
                        <Button
                          size="xs"
                          leftIcon={<FaDownload size={10} />}
                          colorScheme="blue"
                          variant="outline"
                          display={"none"}
                          onClick={() =>
                            askPrice((wp) =>
                              downloadBarcodeLabelPdf(
                                defaultEntries.map((e) => e.label),
                                wp,
                                labelFileNameFromProduct(codeMode, product),
                                codeMode,
                              ),
                            )
                          }>
                          Download All
                        </Button>
                      </Flex>
                    )}

                    <Accordion allowMultiple defaultIndex={[]}>
                      {defaultEntries.map(({ item, barcode, label }) => {
                        const isParent = item.id === product.id;
                        return (
                          <AccordionItem key={item.id} border="none" mb={2}>
                            <AccordionButton
                              bg={isParent ? 'purple.500' : 'blue.500'}
                              color="white"
                              borderRadius="md"
                              _hover={{ opacity: 0.9 }}
                              _expanded={{ borderBottomRadius: 0 }}
                              px={3}
                              py={2}>
                              <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
                                {productDisplayName(item)}
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>

                            <AccordionPanel
                              pb={4}
                              px={3}
                              border="1px solid"
                              borderColor="gray.200"
                              borderTop="none"
                              borderBottomRadius="md">
                              {/* Default barcode info */}
                              <Box
                                p={4}
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200"
                                mb={2}
                                mt={2}>
                                <HStack justify="space-between" align="flex-start">
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      fontWeight="medium">
                                      Default Barcode
                                    </Text>
                                    <Text
                                      fontSize="lg"
                                      fontFamily="mono"
                                      fontWeight="bold"
                                      mt={1}>
                                      {barcode.value}
                                    </Text>
                                    {barcode.description && (
                                      <Text fontSize="xs" color="gray.500" mt={1}>
                                        {barcode.description}
                                      </Text>
                                    )}
                                  </Box>
                                  <Box color="gray.400" pt={1}>
                                    <FaBarcode size={28} />
                                  </Box>
                                </HStack>
                              </Box>

                              {/* Label preview */}
                              <Box>
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="gray.600">
                                    Label Preview
                                  </Text>
                                  <Button
                                    size="xs"
                                    leftIcon={<FaDownload size={10} />}
                                    colorScheme="blue"
                                    variant="outline"
                                    display={"none"}
                                    onClick={() =>
                                      askPrice((wp) =>
                                        downloadBarcodeLabelPdf(
                                          [label],
                                          wp,
                                          labelFileNameFromProduct(codeMode, item),
                                          codeMode,
                                        ),
                                      )
                                    }>
                                    Download
                                  </Button>
                                </Flex>
                                <BarcodeLabelPreview
                                  label={label}
                                  includePrice={true}
                                  onAskPrice={askPrice}
                                  codeMode={codeMode}
                                />
                              </Box>
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </>
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={4}>
                    No default barcode found.
                  </Text>
                )}
              </VStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                {/* Global download - Disabled for now */}
                <Flex justify="flex-end" display={"none"}>
                  <Button
                    size="sm"
                    leftIcon={<FaDownload />}
                    colorScheme="blue"
                    onClick={handleDownloadAll}>
                    Download All Labels
                  </Button>
                </Flex>

                <Accordion allowMultiple defaultIndex={[]}>
                  {/* Parent */}
                  {renderSection(product, 'purple.500', productDisplayName(product) ?? 'Parent')}

                  {/* Children */}
                  {children.map((child) =>
                    renderSection(
                      child,
                      'blue.500',
                      productDisplayName(child) ?? 'Child',
                    ),
                  )}
                </Accordion>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Price confirmation dialog */}
      <AlertDialog
        isOpen={priceDialogOpen}
        leastDestructiveRef={cancelRef as React.RefObject<HTMLElement>}
        onClose={closePriceDialog}
        isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md">
              Include Price on Labels?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Would you like to print the price on the barcode labels?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef as React.RefObject<HTMLButtonElement>}
                onClick={() => answerPrice(false)}
                size="sm">
                No Price
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => answerPrice(true)}
                ml={3}
                size="sm">
                Include Price
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default BarcodeManagerModal;
